import { ChartNoAxesCombined, ChevronDown, ChevronRight } from "lucide-react";
import type { Account, Budget, BudgetOwner, DashboardData, EntryPreset, Owner, Recurring, Transaction } from "../../types";
import type { TransactionFilters, View } from "../../app/views";
import type { CategoryChartStyle, DailyChartStyle } from "../../app/use-overview-preferences";
import SpendingChart from "./charts/SpendingChart";
import SpendingRhythm from "./modules/SpendingRhythm";
import MonthlySpending from "./modules/MonthlySpending";
import NetPosition from "./modules/NetPosition";
import MoneyOwed from "./modules/MoneyOwed";
import CategoryBreakdown from "./modules/CategoryBreakdown";
import LatestActivity from "./modules/LatestActivity";
import Upcoming from "./modules/Upcoming";
import ActiveTripCard from "./modules/ActiveTripCard";
import PartnerSettlement from "./modules/PartnerSettlement";
import GoalsModule from "./modules/GoalsModule";
import { useProfileNames } from "../../components/ProfilesContext";

// The home screen: one module per section, each in src/overview/. Their numbers are worked out in
// overview-data.ts, which is where to look for how any of them are arrived at; the modules are how
// they are laid out. Props are grouped rather than listed flat because a dashboard genuinely draws
// on this much — and the grouping says which parts belong together instead of leaving twenty-odd
// names in a row.

export type OverviewModuleKey = "monthly" | "categories" | "daily" | "rhythm" | "position" | "ious" | "activity" | "upcoming" | "settlement" | "goals";

export interface OverviewProps {
  data: DashboardData;
  month: string;
  monthName: string;
  owner: Owner | "All";
  today: string;
  /** Accounts the owner filter has selected — the position and the loans both scope to these. */
  selectedAccounts: Account[];
  ledger: {
    monthTransactions: Transaction[];
    expenses: Transaction[];
    spending: number | null;
    income: number | null;
    trackedCounts: Map<string, number>;
  };
  chart: {
    /** [label, amount], already sorted by amount. Labels are categories, or subcategories once
     *  one category is selected. */
    categories: [string, number][];
    chartTotal: number | null;
    chartCategory: string | null;
    setChartCategory: (name: string | null) => void;
    categoryChartStyle: CategoryChartStyle;
    setCategoryChartStyle: (style: CategoryChartStyle) => void;
    dailyChartStyle: DailyChartStyle;
    setDailyChartStyle: (style: DailyChartStyle) => void;
    openCategory: (name: string) => void;
  };
  budgets: {
    budgetOwner: BudgetOwner;
    overallBudget: Budget | undefined;
    categoryBudgetRows: { category: string; budget: Budget }[];
  };
  collapse: {
    overviewCollapsed: Record<OverviewModuleKey, boolean>;
    toggleOverviewModule: (key: OverviewModuleKey) => void;
  };
  actions: {
    nav: (view: View) => void;
    setDetail: (t: Transaction | null) => void;
    /** Opens the transactions view already filtered — every drill-down here goes through it. */
    showTransactions: (filters: TransactionFilters) => void;
    onPayBill: (bill: Recurring) => void;
    onAddTransaction?: (prefill: EntryPreset) => void;
    onOpenTrip?: (tripId: string) => void;
  };
}

export default function Overview(props: OverviewProps) {
  const profileNames = useProfileNames();
  const { data, month, monthName, owner, today, selectedAccounts } = props;
  const { monthTransactions, expenses, spending, income, trackedCounts } = props.ledger;
  const { budgetOwner, overallBudget, categoryBudgetRows } = props.budgets;
  const { overviewCollapsed, toggleOverviewModule } = props.collapse;
  const collapseButton = (module: OverviewModuleKey, label: string) => (
    <button className="overview-collapse-button" aria-label={`${overviewCollapsed[module] ? "Expand" : "Collapse"} ${label}`} aria-expanded={!overviewCollapsed[module]} onClick={() => toggleOverviewModule(module)}>
      <ChevronDown size={17} />
    </button>
  );
  const { nav, setDetail, showTransactions, onPayBill, onAddTransaction, onOpenTrip } = props.actions;
  const activeTrips = (data.trips || []).filter((t) => t.status === "active");

  return (
    <div className="overview-modules">
      <button type="button" className="overview-insights-link" onClick={() => nav("insights")}>
        <ChartNoAxesCombined size={20} />
        <span><strong>Insights</strong><small>Trends, patterns and a little perspective.</small></span>
        <ChevronRight size={18} />
      </button>
      {activeTrips.map((trip) => (
        <ActiveTripCard
          key={trip.id}
          trip={trip}
          data={data}
          onAddTransaction={onAddTransaction}
          onOpenTrip={onOpenTrip}
        />
      ))}
      <SpendingChart
        transactions={data.transactions}
        currentMonth={month}
        owner={owner}
        today={today}
        baseCurrency={data.baseCurrency}
        chartStyle={props.chart.dailyChartStyle}
        onChartStyleChange={props.chart.setDailyChartStyle}
        collapsed={overviewCollapsed.daily}
        onToggle={() => toggleOverviewModule("daily")}
      />
      <SpendingRhythm
        expenses={expenses} month={month} today={today}
        collapsed={overviewCollapsed.rhythm} collapseButton={collapseButton("rhythm", "Spending rhythm")}
        showTransactions={showTransactions}
      />
      <div className="summary-grid">
        <MonthlySpending
          transactions={data.transactions} expenses={expenses} spending={spending} income={income}
          month={month} monthName={monthName}
          budgetOwner={budgetOwner} overallBudget={overallBudget} categoryBudgetRows={categoryBudgetRows}
          collapsed={overviewCollapsed.monthly} collapseButton={collapseButton("monthly", "Monthly spending")}
          showTransactions={showTransactions}
        />
        <NetPosition
          selectedAccounts={selectedAccounts} usdRate={data.rates?.USD}
          collapsed={overviewCollapsed.position} collapseButton={collapseButton("position", "The bigger picture")}
        />
        <MoneyOwed
          ious={data.ious || []} selectedAccounts={selectedAccounts}
          collapsed={overviewCollapsed.ious} collapseButton={collapseButton("ious", "Money owed")} nav={nav}
        />
        <GoalsModule
          goals={data.goals || []}
          today={today}
          baseCurrency={data.baseCurrency}
          collapsed={overviewCollapsed.goals}
          collapseButton={collapseButton("goals", "Savings goals")}
          nav={nav}
        />
        <PartnerSettlement
          summary={data.partnerSettlement}
          transactions={data.transactions}
          accounts={data.accounts}
          profiles={profileNames}
          baseCurrency={data.baseCurrency}
          collapsed={overviewCollapsed.settlement}
          collapseButton={collapseButton("settlement", "Partner balance")}
          onSettleUp={(debt) => {
            const fromAcc = data.accounts.find((a) => a.active !== false && a.owner === debt.from);
            const toAcc = data.accounts.find((a) => a.active !== false && a.owner === debt.to);
            onAddTransaction?.({
              type: "Transfer",
              accountId: fromAcc?.id,
              toAccountId: toAcc?.id,
              amount: debt.amount.toFixed(2),
              isSettlement: true,
              notes: `Settlement: ${debt.from} to ${debt.to}`,
            });
          }}
        />
      </div>
      <div className="overview-grid">
        <CategoryBreakdown
          chart={props.chart} expenses={expenses} budgets={data.budgets} budgetOwner={budgetOwner} month={month}
          collapsed={overviewCollapsed.categories} collapseButton={collapseButton("categories", "Where it went")}
        />
        <div className="overview-right">
          <LatestActivity
            monthTransactions={monthTransactions} accounts={data.accounts} payees={data.payees} trips={data.trips} trackedCounts={trackedCounts}
            collapsed={overviewCollapsed.activity} collapseButton={collapseButton("activity", "Latest activity")}
            setDetail={setDetail} showTransactions={showTransactions}
          />
          <Upcoming
            data={data} owner={owner} today={today}
            collapsed={overviewCollapsed.upcoming} collapseButton={collapseButton("upcoming", "On the horizon")}
            onPayBill={onPayBill}
          />
        </div>
      </div>
    </div>
  );
}
